import { useState } from 'react'
import { Mail, MessageSquare, HelpCircle, FileText } from 'lucide-react'
import ContactFormModal from '../components/ContactFormModal'

export default function Contact() {
  const [modalOpen, setModalOpen] = useState(false)
  const [defaultSubject, setDefaultSubject] = useState('')

  const handleContactClick = (subject?: string) => {
    if (subject) {
      setDefaultSubject(subject)
    }
    setModalOpen(true)
  }

  const contactReasons = [
    {
      icon: MessageSquare,
      title: 'General Inquiry',
      description: 'Have a question about our platform or services?',
      subject: 'General Inquiry'
    },
    {
      icon: HelpCircle,
      title: 'Technical Support',
      description: 'Need help with a feature or experiencing issues?',
      subject: 'Technical Support'
    },
    {
      icon: FileText,
      title: 'Feedback',
      description: 'Share your thoughts and suggestions with us',
      subject: 'Product Feedback'
    },
    {
      icon: Mail,
      title: 'Other',
      description: 'Anything else you\'d like to discuss?',
      subject: ''
    }
  ]

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900">Contact Us</h1>
        <p className="mt-4 text-lg text-gray-600">
          We're here to help. Choose a category below or send us a message.
        </p>
      </div>

      {/* Contact Reasons Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {contactReasons.map((reason) => {
          const Icon = reason.icon
          return (
            <button
              key={reason.title}
              onClick={() => handleContactClick(reason.subject)}
              className="bg-white rounded-lg shadow-md hover:shadow-lg transition-all p-6 text-left border border-gray-200 hover:border-indigo-300"
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <Icon className="w-6 h-6 text-indigo-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {reason.title}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {reason.description}
                  </p>
                </div>
              </div>
            </button>
          )
        })}
      </div>

      {/* Direct Contact Section */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg shadow-lg p-8 text-white text-center">
        <h2 className="text-2xl font-bold mb-2">Ready to get in touch?</h2>
        <p className="text-indigo-100 mb-6">
          Click below to open our contact form and we'll respond as soon as possible.
        </p>
        <button
          onClick={() => handleContactClick()}
          className="bg-white text-indigo-600 px-8 py-3 rounded-lg font-semibold hover:bg-indigo-50 transition-colors inline-flex items-center space-x-2"
        >
          <Mail className="w-5 h-5" />
          <span>Send us a Message</span>
        </button>
      </div>

      {/* FAQ Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Frequently Asked Questions
        </h2>
        <div className="space-y-4">
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">
              How quickly will I receive a response?
            </h3>
            <p className="text-sm text-gray-600">
              We typically respond to all inquiries within 24-48 hours during business days.
              For urgent technical issues, please mark your message as "Technical Support".
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">
              Can I schedule a demo?
            </h3>
            <p className="text-sm text-gray-600">
              Absolutely! Send us a message with "Demo Request" in the subject line and let us
              know your availability. We'll set up a personalized demo session for you.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">
              Do you offer custom solutions?
            </h3>
            <p className="text-sm text-gray-600">
              Yes, we work with enterprise clients to provide custom solutions tailored to
              their specific needs. Contact us to discuss your requirements.
            </p>
          </div>
        </div>
      </div>

      {/* Contact Form Modal */}
      <ContactFormModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false)
          setDefaultSubject('')
        }}
        defaultSubject={defaultSubject}
      />
    </div>
  )
}
